import {useSyntheticUsersInfiniteQuery} from './useSyntheticUsersQuery';
import {useSyntheticUsersSearch} from './useSyntheticUsersSearch';
import {useSyntheticUsersFilter} from './useSyntheticUsersFilter';
import {useState} from 'react';
import {DataTableSortStatus} from 'mantine-datatable';
// The comparator is SHARED (FNP-912) — nulls-last, numeric-vs-string and the
// empty-string rule live in one place rather than being re-derived per table.
import {sortRecords} from '@flexinet/ui-components';
import {SyntheticUser} from '../types';
// The DEFAULT stays local: which key to sort by is a per-entity judgement (this
// module sorts on `bornAt` for reasons specific to its schema), whereas HOW to
// compare two values is not.
import {DEFAULT_SORT_STATUS} from '../utils/sortRecords';

const matchesKeyword = (item: SyntheticUser, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    // secretArn is deliberately not part of the record and so cannot be searched.
    const haystack = [
        item.email,
        item.personaId,
        item.lifecycleStage,
        item.state,
        item.accountId,
        item.cognitoSub,
        item.id,
    ];
    return haystack.some(value => typeof value === 'string' && value.toLowerCase()
                                                                    .includes(needle));
};

export const useSyntheticUsers = () => {
    const {filters} = useSyntheticUsersFilter();
    const query = useSyntheticUsersInfiniteQuery(filters);

    // Component state, not the module-scoped store: sort order is a property of
    // how ONE operator is looking at the table right now, unlike the keyword and
    // filters, which are shared across sibling components (FNP-815).
    const [sortStatus, setSortStatus] =
        useState<DataTableSortStatus<SyntheticUser>>(DEFAULT_SORT_STATUS);

    // Keyword comes from the module-scoped store, so what is typed into the toolbar
    // in a SIBLING component actually reaches this filter (FNP-815).
    const {keyword} = useSyntheticUsersSearch();

    const items: SyntheticUser[] = (query.data?.pages?.flatMap(page => page?.items ?? []) ?? [])
        .filter(Boolean);

    const needle = keyword.trim()
                          .toLowerCase();

    // NEWEST-CREATED FIRST by default (owner, 2026-08-11), and every column is
    // sortable from the header.
    //
    // Was sortByNextWake — "who wakes next", the FNP-861 default. The roster
    // reads as a list of users, and the question asked of a list of users is
    // "what is the latest one". The operational view is not lost: `nextWakeAt`
    // is a sortable column like any other, so it is one click away rather than
    // being the only thing on offer.
    //
    // The API already returns created DESC (listSyntheticUsers queries
    // record-type-created-index with scanIndexForward: false), so the default
    // AGREES with the server rather than fighting it. It is still applied
    // client-side because this is an INFINITE QUERY: pages are concatenated as
    // they arrive, and relying on arrival order would degrade silently if a
    // page resolved out of order or a refetch merged pages.
    //
    // Sorting is client-side, over the pages LOADED SO FAR — the same scope the
    // keyword filter already works on. It cannot reorder rows the browser has
    // not fetched, which is a real limit of token pagination, not an oversight.
    const visibleItems = sortRecords(
        items.filter(item => matchesKeyword(item, needle)),
        sortStatus
    );

    return {
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchRecords: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
    };
};
